import { collection, doc, getDocs, getDoc, addDoc, updateDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';

export interface Subject {
    id: string;
    name: string;
    classId: string; // Link to parent class
    createdAt: Date;
    updatedAt: Date;
}

export interface Class {
    id: string;
    grade: string;
    section: string;
    subjects: string[]; // Array of subject names
    createdAt: Date;
    updatedAt: Date;
}

// Subjects CRUD operations
export const getSubjects = async (classId?: string): Promise<Subject[]> => {
    try {
        const subjectsRef = collection(db, 'subjects');
        let q;

        if (classId) {
            // Get subjects for a specific class
            q = query(subjectsRef, orderBy('name', 'asc'));
        } else {
            // Get all subjects
            q = query(subjectsRef, orderBy('name', 'asc'));
        }

        const querySnapshot = await getDocs(q);
        let subjects = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate() || new Date(),
            updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        })) as Subject[];

        // Filter by classId if provided
        if (classId) {
            subjects = subjects.filter(subject => subject.classId === classId);
        }

        return subjects;
    } catch (error) {
        console.error('Error fetching subjects:', error);
        return [];
    }
};

export const addSubject = async (subjectData: Omit<Subject, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    try {
        const subjectsRef = collection(db, 'subjects');
        const docRef = await addDoc(subjectsRef, {
            name: subjectData.name,
            classId: subjectData.classId,
            createdAt: new Date(),
            updatedAt: new Date(),
        });
        return docRef.id;
    } catch (error) {
        console.error('Error adding subject:', error);
        throw error;
    }
};

export const updateSubject = async (id: string, subjectData: Partial<Subject>): Promise<void> => {
    try {
        const subjectRef = doc(db, 'subjects', id);
        await updateDoc(subjectRef, {
            name: subjectData.name,
            classId: subjectData.classId,
            updatedAt: new Date(),
        });
    } catch (error) {
        console.error('Error updating subject:', error);
        throw error;
    }
};

export const deleteSubject = async (id: string): Promise<void> => {
    try {
        const subjectRef = doc(db, 'subjects', id);
        await deleteDoc(subjectRef);
    } catch (error) {
        console.error('Error deleting subject:', error);
        throw error;
    }
};

// Classes CRUD operations
export const getClasses = async (): Promise<Class[]> => {
    try {
        const classesRef = collection(db, 'classes');
        // Try without orderBy first to see if that's the issue
        const querySnapshot = await getDocs(classesRef);

        const classes = querySnapshot.docs.map(doc => {
            const data = doc.data();
            console.log('Raw class data:', { id: doc.id, ...data });
            return {
                id: doc.id,
                ...data,
                createdAt: data.createdAt?.toDate() || new Date(),
                updatedAt: data.updatedAt?.toDate() || new Date(),
            };
        }) as Class[];

        // Sort manually instead of using Firestore orderBy
        classes.sort((a, b) => {
            if (a.grade !== b.grade) {
                return a.grade.localeCompare(b.grade);
            }
            return a.section.localeCompare(b.section);
        });

        console.log('Processed classes:', classes);
        return classes;
    } catch (error) {
        console.error('Error fetching classes:', error);
        return [];
    }
};

export const addClass = async (classData: Omit<Class, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    try {
        console.log('Adding class with data:', classData);
        const classesRef = collection(db, 'classes');
        const docRef = await addDoc(classesRef, {
            ...classData,
            subjects: [], // Initialize with empty subjects array
            createdAt: new Date(),
            updatedAt: new Date(),
        });
        console.log('Class added with ID:', docRef.id);
        return docRef.id;
    } catch (error) {
        console.error('Error adding class:', error);
        throw error;
    }
};

export const updateClass = async (id: string, classData: Partial<Class>): Promise<void> => {
    try {
        const classRef = doc(db, 'classes', id);
        await updateDoc(classRef, {
            ...classData,
            updatedAt: new Date(),
        });
    } catch (error) {
        console.error('Error updating class:', error);
        throw error;
    }
};

export const deleteClass = async (id: string): Promise<void> => {
    try {
        const classRef = doc(db, 'classes', id);
        await deleteDoc(classRef);
    } catch (error) {
        console.error('Error deleting class:', error);
        throw error;
    }
};

// Subject management within classes
export const addSubjectToClass = async (classId: string, subjectName: string): Promise<void> => {
    try {
        const classRef = doc(db, 'classes', classId);
        const classDoc = await getDoc(classRef);

        if (classDoc.exists()) {
            const classData = classDoc.data();
            const currentSubjects = classData.subjects || [];

            // Check if subject already exists
            if (!currentSubjects.includes(subjectName)) {
                await updateDoc(classRef, {
                    subjects: [...currentSubjects, subjectName],
                    updatedAt: new Date(),
                });
            }
        }
    } catch (error) {
        console.error('Error adding subject to class:', error);
        throw error;
    }
};

export const removeSubjectFromClass = async (classId: string, subjectName: string): Promise<void> => {
    try {
        const classRef = doc(db, 'classes', classId);
        const classDoc = await getDoc(classRef);

        if (classDoc.exists()) {
            const classData = classDoc.data();
            const currentSubjects = classData.subjects || [];

            await updateDoc(classRef, {
                subjects: currentSubjects.filter((subject: string) => subject !== subjectName),
                updatedAt: new Date(),
            });
        }
    } catch (error) {
        console.error('Error removing subject from class:', error);
        throw error;
    }
};

export const editSubjectInClass = async (classId: string, oldSubjectName: string, newSubjectName: string): Promise<void> => {
    try {
        const classRef = doc(db, 'classes', classId);
        const classDoc = await getDoc(classRef);

        if (classDoc.exists()) {
            const classData = classDoc.data();
            const currentSubjects = classData.subjects || [];

            const updatedSubjects = currentSubjects.map((subject: string) => 
                subject === oldSubjectName ? newSubjectName : subject
            );

            await updateDoc(classRef, {
                subjects: updatedSubjects,
                updatedAt: new Date(),
            });
        }
    } catch (error) {
        console.error('Error editing subject in class:', error);
        throw error;
    }
};


// Get subjects for a specific class
export const getSubjectsForClass = async (classId: string): Promise<Subject[]> => {
    try {
        return await getSubjects(classId);
    } catch (error) {
        console.error('Error fetching subjects for class:', error);
        return [];
    }
};
